/**
 * Organize Action Constants
 *
 * Defines balance constants for the Organize party action.
 * Organization affects election performance through ground game effectiveness.
 */

// ============================================================================
// Types
// ============================================================================

export type OrganizingFocus =
  | 'volunteer_recruitment'
  | 'infrastructure'
  | 'network_optimization'
  | 'automatic'

export type DistrictSortOption =
  | 'region'
  | 'organization_asc'
  | 'organization_desc'
  | 'population'
  | 'recognition'

// ============================================================================
// Focus Configuration
// ============================================================================

export interface OrganizingFocusConfig {
  key: OrganizingFocus
  label: string
  description: string
  icon: string
  bonusCondition: string
  bonusRange: [number, number] // [min_org, max_org] where bonus applies
  bonusAmount: number
}

export const ORGANIZING_FOCUSES: Record<OrganizingFocus, OrganizingFocusConfig> =
  {
    automatic: {
      key: 'automatic',
      label: 'Automatic',
      description: 'Let organizers choose the best approach',
      icon: '🎯',
      bonusCondition: 'always optimal',
      bonusRange: [0, 100],
      bonusAmount: 0, // Gets bonus based on current organization level
    },
    volunteer_recruitment: {
      key: 'volunteer_recruitment',
      label: 'Volunteer Recruitment',
      description: 'Focus on building a base of dedicated volunteers',
      icon: '👥',
      bonusCondition: 'organization < 30',
      bonusRange: [0, 30],
      bonusAmount: 3,
    },
    infrastructure: {
      key: 'infrastructure',
      label: 'Infrastructure',
      description: 'Establish offices, communications, and logistics',
      icon: '🏢',
      bonusCondition: 'organization 30-60',
      bonusRange: [30, 60],
      bonusAmount: 3,
    },
    network_optimization: {
      key: 'network_optimization',
      label: 'Network Optimization',
      description: 'Improve efficiency and reduce diminishing returns',
      icon: '🔄',
      bonusCondition: 'organization > 60',
      bonusRange: [60, 100],
      bonusAmount: 0, // Special: reduces diminishing returns instead
    },
  }

export const FOCUS_ORDER: OrganizingFocus[] = [
  'automatic',
  'volunteer_recruitment',
  'infrastructure',
  'network_optimization',
]

// ============================================================================
// Effect Constants
// ============================================================================

export const ORGANIZE_EFFECTS = {
  // Base gains per AP level
  BASE_GAIN_2AP: 8,
  BASE_GAIN_3AP: 12,
  BASE_GAIN_4AP: 15,

  // Focus bonus
  FOCUS_BONUS: 3,

  // Diminishing returns
  DIMINISHING_DIVISOR: 150, // factor = max(0.2, 1.0 - currentOrg / 150)
  DIMINISHING_FLOOR: 0.2,

  // Network optimization diminishing reduction
  NETWORK_OPT_DIVISOR: 200, // Use 200 instead of 150 for better returns

  // Warning thresholds
  HIGH_ORG_WARNING_THRESHOLD: 60,

  // AP range
  MIN_AP: 2,
  MAX_AP: 4,

  // Organization limits
  ORG_MIN: 0,
  ORG_MAX: 100,

  // Settlement type modifiers
  // @tuning Adjust to balance organizing difficulty by settlement type
  SETTLEMENT_MODIFIER_RURAL: 1.1, // Easier to build tight-knit networks
  SETTLEMENT_MODIFIER_SUBURBAN: 1.0, // Baseline
  SETTLEMENT_MODIFIER_URBAN: 0.9, // Harder to organize, more transient population

  // Regional candidate bonus
  // @tuning Having a candidate assigned to the district helps organizing
  REGIONAL_CANDIDATE_BONUS: 2,

  // Milestone thresholds
  MILESTONE_BASIC_PRESENCE: 25, // "Basic presence established"
  MILESTONE_VOLUNTEER_NETWORK: 50, // "Functioning volunteer network"
  MILESTONE_STRONG_GROUND_GAME: 75, // "Strong ground game"
  MILESTONE_DOMINANT_ORGANIZATION: 90, // "Dominant local organization"
}

// ============================================================================
// Sort Options
// ============================================================================

export const DISTRICT_SORT_OPTIONS: { key: DistrictSortOption; label: string }[] = [
  { key: 'organization_asc', label: 'Organization (Low to High)' },
  { key: 'organization_desc', label: 'Organization (High to Low)' },
  { key: 'region', label: 'By Region' },
  { key: 'population', label: 'Population (High to Low)' },
  { key: 'recognition', label: 'Recognition (High to Low)' },
]

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get base organization gain for given AP intensity.
 */
export function getBaseGain(intensity: number): number {
  switch (intensity) {
    case 2:
      return ORGANIZE_EFFECTS.BASE_GAIN_2AP
    case 3:
      return ORGANIZE_EFFECTS.BASE_GAIN_3AP
    case 4:
      return ORGANIZE_EFFECTS.BASE_GAIN_4AP
    default:
      return ORGANIZE_EFFECTS.BASE_GAIN_2AP
  }
}

/**
 * Calculate diminishing returns factor.
 */
export function getDiminishingFactor(
  currentOrganization: number,
  focus?: OrganizingFocus
): number {
  const divisor =
    focus === 'network_optimization' && currentOrganization > 60
      ? ORGANIZE_EFFECTS.NETWORK_OPT_DIVISOR
      : ORGANIZE_EFFECTS.DIMINISHING_DIVISOR

  return Math.max(
    ORGANIZE_EFFECTS.DIMINISHING_FLOOR,
    1.0 - currentOrganization / divisor
  )
}

/**
 * Calculate focus bonus based on current organization and selected focus.
 */
export function getFocusBonus(
  currentOrganization: number,
  focus: OrganizingFocus | ''
): number {
  if (!focus || focus === 'network_optimization') return 0

  if (focus === 'automatic') {
    // Automatic gets bonus if any condition is met
    if (currentOrganization < 30) return ORGANIZE_EFFECTS.FOCUS_BONUS
    if (currentOrganization >= 30 && currentOrganization <= 60)
      return ORGANIZE_EFFECTS.FOCUS_BONUS
    return 0
  }

  const config = ORGANIZING_FOCUSES[focus]
  const [min, max] = config.bonusRange

  if (currentOrganization >= min && currentOrganization < max) {
    return config.bonusAmount
  }

  return 0
}

/**
 * Get organization level description for UI.
 */
export function getOrganizationBenefit(organization: number): string {
  if (organization >= 76) {
    return 'Excellent ground game - maximum GOTV effectiveness'
  } else if (organization >= 51) {
    return 'Strong infrastructure - significant GOTV bonus'
  } else if (organization >= 26) {
    return 'Moderate presence - noticeable campaign benefits'
  } else {
    return 'Minimal presence - limited local effectiveness'
  }
}

/**
 * Get efficiency rating based on organization level.
 */
export function getEfficiencyRating(currentOrganization: number): {
  rating: 'excellent' | 'good' | 'moderate' | 'low'
  reason: string
} {
  if (currentOrganization >= 80) {
    return {
      rating: 'low',
      reason: 'Already very organized - diminishing returns',
    }
  } else if (currentOrganization >= 60) {
    return { rating: 'moderate', reason: 'High organization reduces gains' }
  } else if (currentOrganization >= 30) {
    return { rating: 'good', reason: 'Good potential for growth' }
  } else {
    return { rating: 'excellent', reason: 'Low organization means high impact' }
  }
}

/**
 * Get color class for organization bar based on level.
 */
export function getOrganizationBarColor(organization: number): string {
  if (organization >= 60) return 'bg-green-500'
  if (organization >= 40) return 'bg-blue-500'
  if (organization >= 20) return 'bg-amber-500'
  return 'bg-red-500'
}

/**
 * Get settlement type modifier for organization gains.
 */
export function getSettlementModifier(
  settlementType: 'rural' | 'suburban' | 'urban'
): number {
  switch (settlementType) {
    case 'rural':
      return ORGANIZE_EFFECTS.SETTLEMENT_MODIFIER_RURAL
    case 'suburban':
      return ORGANIZE_EFFECTS.SETTLEMENT_MODIFIER_SUBURBAN
    case 'urban':
      return ORGANIZE_EFFECTS.SETTLEMENT_MODIFIER_URBAN
    default:
      return ORGANIZE_EFFECTS.SETTLEMENT_MODIFIER_SUBURBAN
  }
}

/**
 * Check if organization crossed a milestone threshold.
 * Returns the milestone description if crossed, null otherwise.
 */
export function getOrganizationMilestone(
  previousOrg: number,
  newOrg: number
): string | null {
  // Check each milestone in ascending order
  const milestones = [
    {
      threshold: ORGANIZE_EFFECTS.MILESTONE_BASIC_PRESENCE,
      description: 'Basic presence established',
    },
    {
      threshold: ORGANIZE_EFFECTS.MILESTONE_VOLUNTEER_NETWORK,
      description: 'Functioning volunteer network',
    },
    {
      threshold: ORGANIZE_EFFECTS.MILESTONE_STRONG_GROUND_GAME,
      description: 'Strong ground game',
    },
    {
      threshold: ORGANIZE_EFFECTS.MILESTONE_DOMINANT_ORGANIZATION,
      description: 'Dominant local organization',
    },
  ]

  // Find the highest milestone crossed
  let crossedMilestone: string | null = null
  for (const milestone of milestones) {
    if (previousOrg < milestone.threshold && newOrg >= milestone.threshold) {
      crossedMilestone = milestone.description
    }
  }

  return crossedMilestone
}

/**
 * Determine which focus would be best for automatic selection.
 */
export function getAutomaticFocus(
  currentOrganization: number
): OrganizingFocus {
  if (currentOrganization < 30) {
    return 'volunteer_recruitment'
  } else if (currentOrganization < 60) {
    return 'infrastructure'
  } else {
    return 'network_optimization'
  }
}
